import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SalfordCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Salford Pothole Claims | 35% U-Roads in RED | Fixtyer"
        description="Salford City Council earns GREEN on spend but AMBER overall — with 35% of unclassified roads in RED condition, up from 16% in 2020, and 68,500 potholes filled in five years. See what the DfT rating means for your claim."
        canonical="/councils/salford"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Salford', url: '/councils/salford' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Salford spends nearly three times its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but the ratings that matter for your claim are road condition (AMBER) and best practice (also AMBER). Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. Salford\'s own data shows 35% of unclassified roads in RED condition, up from 16% in 2020, and A-road RED condition more than tripled over the same period.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 561km — 76% of Salford\'s 742km carriageway network. At the latest survey, 35% were in RED condition, up from 16% in 2020. The council surveys one-third of unclassified roads each year by Coarse Visual Inspection, covering the full network every three years — not the automated SCANNER system used on A, B and C roads.',
          },
          {
            question: 'Does Salford admitting less funding caused more potholes help my claim?',
            answer: 'Yes. Salford states that reduced capital funding meant "we haven\'t been able to carry out as much planned maintenance," leading to deteriorating surfaces and "more potholes and a greater need for short term, reactive repairs." That is the council\'s own explanation for why reactive repair volumes nearly doubled in five years — and why condition on residential roads worsened even as spend increased.',
          },
          {
            question: 'Pothole repairs rose to 18,200 in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Salford expects to fill approximately 18,000 potholes in 2025/26 as well. The council filled 68,500 potholes over five years while RED U-roads more than doubled from 16% to 35%. Reactive repairs are a symptom of deterioration, not proof the underlying network is in good condition.',
          },
          {
            question: 'Why is Salford AMBER on best practice as well as condition?',
            answer: 'Unlike councils that earn GREEN across spend and best practice, Salford is AMBER on both condition and best practice while GREEN on spend. That means the DfT sees investment without fully matching process standards. Salford will still argue a structured Section 58 defence through its Highway Infrastructure Asset Management Plan, but with more identifiable gaps than a double-GREEN authority.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Salford City Council"
        councilSlug="salford"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/salford"
        headline="Salford Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Salford</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Salford: 35% of Residential Roads in RED Condition
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Salford City Council outspends its DfT capital allocation every year and earns a{' '}
            <strong className="text-emerald-400">GREEN</strong> spend scorecard. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because{' '}
            <strong className="text-rose-400">35% of unclassified roads are now in RED condition</strong>, up from
            16% in 2020, A-road deterioration has more than tripled, and the council filled{' '}
            <strong className="text-rose-400">68,500 potholes in five years</strong> on a network where
            three-quarters of carriageway is residential.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">35%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2024/25)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 16% in 2020/21 — roughly 196km of Salford&apos;s 561km unclassified network, surveyed
              by visual inspection on a three-year cycle rather than automated SCANNER.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network size */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A 742km Network, Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Salford City Council&apos;s Highway Maintenance Report 2025 to 2026
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center">120</td>
                  <td className="px-6 py-4 text-center">16.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center">61</td>
                  <td className="px-6 py-4 text-center">8.2%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">U roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-bold">561</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">75.6%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageway</td>
                  <td className="px-6 py-4 text-center font-bold">742</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Also in the network</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• 1,109km of footways (surveyed on a five-year cycle)</li>
                <li>• 128km of other public rights of way</li>
                <li>• Gross replacement cost estimated at over £1.1 billion</li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why the split matters</h3>
              <p className="text-amber-800 text-sm">
                Three-quarters of Salford&apos;s carriageway is unclassified — estate roads, residential
                streets and local routes. These are the roads most drivers hit potholes on, and they are
                assessed by a different, less frequent survey method than the classified network.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Salford&apos;s own transparency report — classified roads
            slipping, residential roads in crisis
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (120km — 16% of network): deteriorating</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">18%</td>
                    <td className="px-4 py-2 text-center">80%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">19%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">72%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7%</td>
                    <td className="px-4 py-2 text-center font-bold">24%</td>
                    <td className="px-4 py-2 text-center font-bold">69%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED A-roads more than tripled from 2% to 7% since 2020/21, and good-condition A-roads fell
              from 80% to 69%. These are SCANNER-surveyed every two years — the council should have
              current data on every major route.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (61km — 8% of network): declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">73%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">70%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">68%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold">29%</td>
                    <td className="px-4 py-2 text-center font-bold">66%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED B/C roads doubled from 3% to 6%, and amber rose from 24% to 29%. Over a third of the
              B/C network now needs — or will soon need — maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.9m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£15.7m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">81%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Salford spends nearly <span className="text-emerald-400">three times its DfT allocation</span> — 
              and residential road condition has more than doubled in RED. The problem isn&apos;t the
              chequebook. The network is deteriorating faster than even a GREEN-rated spend programme
              can repair it.
            </p>
          </div>
        </div>
      </section>

      {/* U-road crisis */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 561km Residential Crisis
          </h2>
          <p className="text-surface-600 mb-8">
            76% of Salford&apos;s carriageway is unclassified — and RED condition has more than doubled
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">35%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">35%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The Three-Year CVI Cycle</h3>
              <p className="text-surface-600 text-sm mb-3">
                Salford&apos;s report states that for unclassified roads, trained inspectors carry out
                Coarse Visual Inspections and &ldquo;survey one-third of these roads each year, covering
                the full network every three years.&rdquo; That is a longer gap than the two-year SCANNER
                cycle on classified roads.
              </p>
              <p className="text-surface-600 text-sm">
                At 35% RED, roughly <strong>196km of residential roads</strong> should be considered for
                repair as a priority — yet the council publishes only RED percentages for U-roads, not
                full amber/green splits.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The 2023/24 Step Change</h3>
              <p className="text-amber-800 text-sm mb-3">
                RED U-roads jumped from 21% in 2022/23 to 35% in 2023/24 and held at 35% in 2024/25.
                That is not a blip — it is a structural shift in the condition of three-quarters of
                Salford&apos;s network.
              </p>
              <p className="text-amber-800 text-sm">
                Salford plans to resurface only 15 to 20 kilometres of carriageway in 2025/26 — roughly
                2% of the total network — while expecting to fill approximately 18,000 potholes reactively.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for
              knowing the condition of its roads. For Salford&apos;s unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it within the three-year CVI cycle?</li>
              <li>• If 35% of U-roads are RED, what was done about yours before you hit the pothole?</li>
              <li>• How does a visual inspection every three years catch defects that form between surveys?</li>
              <li>• Does the council&apos;s own admission of reduced planned maintenance explain the defect on your road?</li>
            </ul>
            <p className="text-white font-medium">
              A council that knows more than a third of its residential network is in RED condition
              cannot claim ignorance of the risk on any given U-road.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            68,500 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes nearly doubled as the council admits surfaces deteriorated
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimated)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">9,500</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">11,200</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">11,800</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17,800</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18,200</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">68,500</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~50 Potholes a Day in 2024/25</h3>
              <p className="text-surface-600 text-sm">
                Salford filled 18,200 potholes in 2024/25 — about 50 every day — and expects approximately
                18,000 in 2025/26. The council states these figures are &ldquo;estimated based on orders
                that have been raised following routine highway inspections.&rdquo; A network producing
                defects at that rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Funding Admission</h3>
              <p className="text-amber-800 text-sm">
                The council links rising pothole numbers directly to reduced capital funding for
                resurfacing. Reactive repairs nearly doubled from 9,500 to 18,200 in five years — while
                RED U-roads more than doubled over the same period. The two trends move together.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="Over the past five or six years, there has been less capital funding available for road improvements such as resurfacing and overlaying. This means we haven't been able to carry out as much planned maintenance to keep the roads in the best condition. As a result, road surfaces have begun to deteriorate, leading to more potholes and a greater need for short term, reactive repairs."
            source="Salford City Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Spending table */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Highway Spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital spend consistently outstrips the DfT allocation — yet condition keeps slipping
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£4.38m</td>
                  <td className="px-4 py-3 text-center">£8.19m</td>
                  <td className="px-4 py-3 text-center">£2.59m</td>
                  <td className="px-4 py-3 text-center">79%</td>
                  <td className="px-4 py-3 text-center">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£4.24m</td>
                  <td className="px-4 py-3 text-center">£7.19m</td>
                  <td className="px-4 py-3 text-center">£2.71m</td>
                  <td className="px-4 py-3 text-center">76%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£4.94m</td>
                  <td className="px-4 py-3 text-center">£8.55m</td>
                  <td className="px-4 py-3 text-center">£2.81m</td>
                  <td className="px-4 py-3 text-center">79%</td>
                  <td className="px-4 py-3 text-center">21%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£4.38m</td>
                  <td className="px-4 py-3 text-center">£13.85m</td>
                  <td className="px-4 py-3 text-center">£4.11m</td>
                  <td className="px-4 py-3 text-center">77%</td>
                  <td className="px-4 py-3 text-center">23%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£5.34m</td>
                  <td className="px-4 py-3 text-center">£14.76m</td>
                  <td className="px-4 py-3 text-center">£3.77m</td>
                  <td className="px-4 py-3 text-center">81%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center font-bold">£5.94m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£15.68m</td>
                  <td className="px-4 py-3 text-center font-bold">£4.00m</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">81%</td>
                  <td className="px-4 py-3 text-center font-bold">19%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6">
            <QuoteCard
              quote="In the past, the council used a 'worst-first' method, fixing the most damaged roads first. While this was easy to understand, it led to a growing backlog of repairs."
              source="Salford City Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Based on current trends, condition assessments, and inspection data, Salford City Council expects to fill approximately 18,000 potholes across our road network during the 2025–2026 financial year."
              source="Salford City Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Inspection methodology */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            How Salford Checks Its Roads
          </h2>
          <p className="text-surface-600 mb-8">
            Different survey methods for different road classes — and a national standard change coming
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">SCANNER (A, B and C roads)</h3>
              <p className="text-surface-600 text-sm mb-3">
                Salford uses a laser-based SCANNER system on major roads. The report states these roads
                are &ldquo;surveyed every two years, this follows national guidelines and ensures we have
                up-to-date information on our busiest roads.&rdquo;
              </p>
              <p className="text-surface-600 text-sm">
                With A-road RED condition more than tripling since 2020/21, the question for main-road
                claims is whether the two-year cycle caught deterioration before your incident.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">CVI (unclassified roads)</h3>
              <p className="text-amber-800 text-sm mb-3">
                Trained inspectors carry out Coarse Visual Inspections on U-roads, surveying one-third
                each year. This is a lower-tech, less frequent assessment than SCANNER — on the road
                class where 35% are now in RED condition.
              </p>
              <p className="text-amber-800 text-sm">
                Footways are surveyed on a five-year cycle via Footway Network Surveys — a separate
                blind spot for pavement claims.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-surface-200 mb-8">
            <h3 className="font-semibold text-brand-900 mb-3">PAS 2161 From 2026/27</h3>
            <p className="text-surface-600 text-sm">
              Salford notes that from 2026/27, BSI PAS 2161 will expand the rating system from three
              categories to five. The council states this &ldquo;will give both local authorities and
              the government a clearer, more consistent understanding of road conditions nationwide.&rdquo;
              Until then, the three-tier RED/amber/green data in this report is what governs your claim.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 and the Reasonable Inspection Test</h3>
            <p className="text-brand-200 mb-4">
              Under Section 41 of the Highways Act 1980, Salford must maintain highways reasonably safe
              for ordinary traffic. Section 58 provides a defence if the council can prove it took such
              care as was reasonable. For Salford, that means:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Routine safety inspections ordering repairs where needed</li>
              <li>• Condition surveys on a defined cycle (two years for classified, three for U-roads)</li>
              <li>• A documented asset management plan guiding prioritisation</li>
            </ul>
            <p className="text-white font-medium">
              A GREEN spend scorecard and an asset management plan support the council&apos;s defence.
              But 35% RED U-roads, a admitted repair backlog, and 68,500 potholes in five years are
              evidence the system is not keeping pace with deterioration on the ground.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Salford invests heavily — here&apos;s how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council&apos;s Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — invests nearly three times its DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> ~81% of spend classed as preventative in 2024/25 and 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented Highway Infrastructure Asset Management Plan
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> SCANNER surveys on classified roads every two years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Part of Greater Manchester Road Activity Permit Scheme (GMRAPS)
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 35% of U-roads in RED, up from 16%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice — not the top DfT process tier
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 76% of network on three-year CVI cycle, not SCANNER
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 68,500 potholes filled in five years — defects form faster than inspections catch them
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits reduced resurfacing caused deterioration and more reactive repairs
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Admitted &ldquo;worst-first&rdquo; approach led to a growing backlog of repairs
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a documented asset management plan, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, the three-year CVI gap and 35% RED figure are your strongest structural arguments</li>
              <li>• Whether the council&apos;s own funding admission explains why your road was left to deteriorate</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Salford&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Salford?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road CVI-cycle argument</li>
                <li>• No prior-report search</li>
                <li>• No funding-admission analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 35% RED U-roads documented</li>
                <li>✅ Three-year CVI survey gap argued</li>
                <li>✅ 68,500 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Salford</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=salford"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Salford Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Salford spends nearly three times its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but the ratings that matter for your claim are
                road condition (AMBER) and best practice (also AMBER). Section 58 turns on whether the
                specific defect that damaged your vehicle was reasonably inspected and repaired — not on
                aggregate spend. Salford&apos;s own data shows 35% of unclassified roads in RED condition,
                up from 16% in 2020, and A-road RED condition more than tripled over the same period.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 561km — 76% of Salford&apos;s 742km carriageway network. At the latest
                survey, 35% were in RED condition, up from 16% in 2020. The council surveys one-third of
                unclassified roads each year by Coarse Visual Inspection, covering the full network every
                three years — not the automated SCANNER system used on A, B and C roads.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Salford admitting less funding caused more potholes help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. Salford states that reduced capital funding meant &ldquo;we haven&apos;t been able to
                carry out as much planned maintenance,&rdquo; leading to deteriorating surfaces and
                &ldquo;more potholes and a greater need for short term, reactive repairs.&rdquo; That is
                the council&apos;s own explanation for why reactive repair volumes nearly doubled in five
                years — and why condition on residential roads worsened even as spend increased.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Pothole repairs rose to 18,200 in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Salford expects to fill approximately 18,000 potholes in 2025/26 as well. The council
                filled 68,500 potholes over five years while RED U-roads more than doubled from 16% to 35%.
                Reactive repairs are a symptom of deterioration, not proof the underlying network is in
                good condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why is Salford AMBER on best practice as well as condition?
              </h3>
              <p className="text-surface-600 text-sm">
                Unlike councils that earn GREEN across spend and best practice, Salford is AMBER on both
                condition and best practice while GREEN on spend. That means the DfT sees investment
                without fully matching process standards. Salford will still argue a structured Section 58
                defence through its Highway Infrastructure Asset Management Plan, but with more identifiable
                gaps than a double-GREEN authority.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.salford.gov.uk/highway-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Salford City Council Highway Maintenance Report 2025 to 2026
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
